/**
 * @file GameState.js
 * @description Singleton state manager for the client-side gameplay loop (AppStateManager).
 * Handles transitions between global game modes and stores transient vault data.
 * NOTE: This is client-side state only. Backend is the source of truth for vault data.
 */

import { GAME_STATES, TILE_STATUS } from '../constants';
import { BOOTCAMP_CONFIG, BOOTCAMP_STORAGE_KEY } from '../config/bootcampConfig';
import {
    MYSTERY_TIER,
    PUBLIC_ATELIER_ID,
    PUBLIC_WORKSHOP_ID,
    normalizeTier,
    publicSyndicateForTier,
} from '../config/tiers';

/**
 * In-memory store for the current Mosaic Grid status.
 * Handles read/write operations for tile data.
 */
class VaultStore {
    constructor() {
        this.tiles = new Map(); // id -> Tile
    }

    /**
     * Loads raw vault data into the store.
     * @param {Object} vaultData 
     */
    load(vaultData) {
        if (!vaultData || !vaultData.grid) return;
        // Keep claimer solutionGrid across polls if a response omits it
        const keptSolutions = new Map();
        for (const t of this.tiles.values()) {
            if (t?.data?.solutionGrid) keptSolutions.set(t.id, t.data.solutionGrid);
        }
        this.tiles.clear();
        vaultData.grid.forEach((tile) => {
            const next = { ...tile, data: { ...(tile.data || {}) } };
            if (!next.data.solutionGrid && keptSolutions.has(tile.id)) {
                next.data.solutionGrid = keptSolutions.get(tile.id);
            }
            this.tiles.set(tile.id, next);
        });
    }

    getTile(tileId) {
        return this.tiles.get(tileId);
    }

    /**
     * Returns an iterator of all tiles.
     * @returns {IterableIterator<Object>}
     */
    getTiles() {
        return this.tiles.values();
    }

    /**
     * Updates a tile's properties.
     * @param {string} tileId 
     * @param {Object} updates 
     * @returns {Object} Updated tile
     */
    updateTile(tileId, updates) {
        const tile = this.tiles.get(tileId);
        if (!tile) return null;
        Object.assign(tile, updates);
        return tile;
    }

    /**
     * Finds any active lock held by a specific user.
     * @param {string} userId 
     * @returns {Object|null} Tile or null
     */
    findActiveLockForUser(userId) {
        for (const tile of this.tiles.values()) {
            if (tile.status === TILE_STATUS.CLAIMED && tile.claimedBy === userId) {
                return tile;
            }
        }
        return null;
    }

    /**
     * Checks for and releases expired locks.
     * @param {number} now - Current timestamp
     * @returns {Array<string>} IDs of released tiles
     */
    checkExpiredLocks(now) {
        const releasedIds = [];
        for (const tile of this.tiles.values()) {
            if (tile.status === TILE_STATUS.CLAIMED && tile.lockExpiry && tile.lockExpiry < now) {
                // Auto-release
                tile.status = TILE_STATUS.OPEN;
                tile.claimedBy = null;
                tile.lockExpiry = null;
                releasedIds.push(tile.id);
            }
        }
        return releasedIds;
    }
}

class GameStateManager {
    constructor() {
        this.currentState = GAME_STATES.LOBBY;
        this.vault = new VaultStore();
        this.currentUser = null;

        this.currentSquadId = localStorage.getItem('vq_syndicate_id') || PUBLIC_WORKSHOP_ID;
        this.currentSquadName = localStorage.getItem('vq_syndicate_name') || 'Public Workshop';
        this.mysteryTier = normalizeTier(
            localStorage.getItem('vq_mystery_tier') || MYSTERY_TIER.WORKSHOP
        );
        this.atelierUnlocked = localStorage.getItem('vq_atelier_unlocked') === '1';
        this.workshopSlicesSolved = Number(localStorage.getItem('vq_workshop_slices') || 0);
        this.workshopHalfClears = Number(localStorage.getItem('vq_workshop_half') || 0);
        this.justUnlockedAtelier = false;

        // UI Spec State Extensions
        this.selectedTileId = null;
        this.isSubmitting = false;
        this.lastError = null;
        this.isBootcampMode = false;
        this.listeners = new Set();
    }

    subscribe(callback) {
        this.listeners.add(callback);
        return () => this.listeners.delete(callback);
    }

    notify() {
        this.listeners.forEach(cb => cb(this.currentState, this));
    }

    /**
     * Initializes the state with user identity.
     * @param {Object} userContext - Identity from Studio Core
     */
    init(userContext) {
        if (!userContext || !userContext.id) {
            console.error('[GameState] Invalid user context');
            return;
        }
        this.currentUser = userContext;
        this.isBootcampMode = false; // Reset by default
        console.log('[GameState] Initialized with user:', userContext.id);
        this.notify();
    }

    /**
     * Updates the current active squad context.
     * @param {string} squadId 
     * @param {string} squadName 
     */
    setSquad(squadId, squadName) {
        this.currentSquadId = squadId;
        this.currentSquadName = squadName;
        localStorage.setItem('vq_syndicate_id', squadId);
        localStorage.setItem('vq_syndicate_name', squadName);
        console.log(`[GameState] Switched to squad: ${squadName} (${squadId})`);
        this.notify();
    }

    /**
     * Apply server progress payload (atelier unlock + preferred tier + bootcamp).
     */
    applyProgress(progress, { announceUnlock = true } = {}) {
        if (!progress) return;
        const wasUnlocked = this.atelierUnlocked;
        this.atelierUnlocked = !!progress.atelierUnlocked;
        this.workshopSlicesSolved = Number(progress.workshopSlicesSolved || 0);
        this.workshopHalfClears = Number(progress.workshopHalfClears || 0);
        localStorage.setItem('vq_atelier_unlocked', this.atelierUnlocked ? '1' : '0');
        localStorage.setItem('vq_workshop_slices', String(this.workshopSlicesSolved));
        localStorage.setItem('vq_workshop_half', String(this.workshopHalfClears));
        if (progress.bootcampCompleted) {
            localStorage.setItem(BOOTCAMP_STORAGE_KEY, 'true');
            if (this.currentUser) this.currentUser.hasCompletedBootcamp = true;
        }
        if (progress.preferredTier) {
            const next = normalizeTier(progress.preferredTier);
            if (this.atelierUnlocked || next === MYSTERY_TIER.WORKSHOP) {
                this.mysteryTier = next;
                localStorage.setItem('vq_mystery_tier', this.mysteryTier);
            }
        }
        if (announceUnlock && progress.justUnlocked && !wasUnlocked) {
            this.justUnlockedAtelier = true;
        }
        this.notify();
    }

    clearUnlockToast() {
        this.justUnlockedAtelier = false;
        this.notify();
    }

    /**
     * Switch Workshop / Atelier lane. Remaps public floor ids.
     */
    setMysteryTier(tier) {
        const next = normalizeTier(tier);
        if (next === MYSTERY_TIER.ATELIER && !this.atelierUnlocked) {
            console.warn('[GameState] Atelier locked');
            return false;
        }
        this.mysteryTier = next;
        localStorage.setItem('vq_mystery_tier', next);
        const onPublic =
            this.currentSquadId === PUBLIC_ATELIER_ID ||
            this.currentSquadId === PUBLIC_WORKSHOP_ID ||
            this.currentSquadId === 'public-syndicate-0000';
        if (onPublic) {
            const id = publicSyndicateForTier(next);
            const name = next === MYSTERY_TIER.WORKSHOP ? 'Public Workshop' : 'Public Atelier';
            this.currentSquadId = id;
            this.currentSquadName = name;
            localStorage.setItem('vq_syndicate_id', id);
            localStorage.setItem('vq_syndicate_name', name);
        }
        this.vault.tiles.clear();
        this.notify();
        return true;
    }

    /**
     * Updates the local store with fresh vault data from server.
     * @param {Object} vaultData - JSON payload from BFF
     */
    syncVault(vaultData) {
        // BOOTCAMP SAFETY: Ignore server updates if we are in Bootcamp
        if (this.isBootcampMode) {
            console.log('[GameState] Ignoring server sync (Bootcamp Mode Active)');
            return;
        }

        this.vault.load(vaultData);

        const tiles = Array.from(this.vault.tiles.values());
        const allSolved =
            tiles.length > 0 && tiles.every((t) => t.status === TILE_STATUS.SOLVED);

        // Reseed / partial vault: leave terminal complete state when slices reopen
        if (
            !allSolved &&
            this.currentState === GAME_STATES.VAULT_COMPLETE
        ) {
            this.transitionTo(GAME_STATES.VAULT_ACTIVE);
            return;
        }

        if (allSolved && !this.isBootcampMode) {
            this.selectedTileId = null;
            this.transitionTo(GAME_STATES.VAULT_COMPLETE);
            return;
        }

        // Auto-transition logic check
        // If we have active data and are in LOBBY, move to ACTIVE
        if (this.vault.tiles.size > 0 && this.currentState === GAME_STATES.LOBBY) {
            this.transitionTo(GAME_STATES.VAULT_ACTIVE);
        } else {
            this.notify(); // Notify even if state didn't change, as data did
        }
    }

    /**
     * Strict transition handler.
     * @param {string} newState - One of GAME_STATES
     */
    transitionTo(newState) {
        console.log(`[GameState] Transition: ${this.currentState} -> ${newState}`);
        this.currentState = newState;
        this.notify();
    }

    getCurrentState() {
        return this.currentState;
    }

    /**
     * Initializes Bootcamp Mode.
     * Loads static config and sets state.
     */
    initBootcamp() {
        console.log('[GameState] Initializing Bootcamp Mode');
        this.isBootcampMode = true;
        this.vault.load(BOOTCAMP_CONFIG);
        this.transitionTo(GAME_STATES.BOOTCAMP);
    }

    // --- UI Spec Actions ---

    selectTile(tileId) {
        // Allow re-selection or switching while focused (Pivot)
        // After reseed mid-session, also accept VAULT_COMPLETE → focus if slices reopen
        if (
            this.currentState !== GAME_STATES.VAULT_ACTIVE &&
            this.currentState !== GAME_STATES.TILE_FOCUSED &&
            this.currentState !== GAME_STATES.PUZZLE_ACTIVE &&
            this.currentState !== GAME_STATES.BOOTCAMP &&
            this.currentState !== GAME_STATES.VAULT_COMPLETE
        ) {
            console.warn(`[GameState] Cannot select tile in state ${this.currentState}`);
            return;
        }
        // Selecting a slice after complete (e.g. stale UI) re-opens active play if any OPEN remain
        if (this.currentState === GAME_STATES.VAULT_COMPLETE) {
            const tiles = Array.from(this.vault.tiles.values());
            const allSolved =
                tiles.length > 0 && tiles.every((t) => t.status === TILE_STATUS.SOLVED);
            if (allSolved) {
                console.warn('[GameState] Vault still complete — reseed to play again');
                return;
            }
            this.currentState = GAME_STATES.VAULT_ACTIVE;
        }
        this.selectedTileId = tileId;
        this.transitionTo(GAME_STATES.TILE_FOCUSED);
    }

    clearSelection() {
        this.selectedTileId = null;
        // Do not leave terminal completion states (last-slice solve + modal close race)
        if (
            this.currentState === GAME_STATES.VAULT_COMPLETE ||
            this.currentState === GAME_STATES.BOOTCAMP_COMPLETE
        ) {
            this.notify();
            return;
        }
        // Return to appropriate parent state
        this.transitionTo(this.isBootcampMode ? GAME_STATES.BOOTCAMP : GAME_STATES.VAULT_ACTIVE);
    }

    startSubmit() {
        this.isSubmitting = true;
        this.transitionTo(GAME_STATES.ACTION_SUBMITTING);
    }

    endSubmit(success) {
        this.isSubmitting = false;
        // Keep puzzle open when a slice is selected; otherwise return to hub play
        if (this.selectedTileId) {
            this.transitionTo(GAME_STATES.TILE_FOCUSED);
            return;
        }
        this.transitionTo(
            this.isBootcampMode ? GAME_STATES.BOOTCAMP : GAME_STATES.VAULT_ACTIVE
        );
    }

    setError(msg) {
        this.lastError = msg;
        const previousState = this.currentState;
        this.transitionTo(GAME_STATES.ERROR_FEEDBACK);

        // Transient state - auto revert after timeout? 
        // For now, UI should handle the display and call clearError() or similar
        // Or we just stay in ERROR_FEEDBACK until user acknowledges?
        // Spec says: ERROR_FEEDBACK -> VAULT_ACTIVE
        setTimeout(() => {
            this.lastError = null;
            this.transitionTo(GAME_STATES.VAULT_ACTIVE);
        }, 3000);
    }

    /**
     * Checks if a user already has a claimed tile in the vault.
     * @param {string} userId 
     * @returns {boolean}
     */
    userHasActiveLock(userId) {
        // Query VaultStore explicitly
        return !!this.vault.findActiveLockForUser(userId);
    }
}

// Export Singleton
export const gameState = new GameStateManager();
